import {
  createSlice,
  createAsyncThunk
} from '@reduxjs/toolkit'

import { selectActivePresenter, slice as activePresenterSlice } from './activePresenter'
// @ts-ignore
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
// @ts-ignore
import { getGroupKey } from '../../../participant-components/register/util/groupKey'
// @ts-ignore
import Registration from '@polleverywhere/core/lib/models/registration'

/**
 * See Api::RegistrationsController for more details on param usage
 */
interface IExtraRegistrationParams { /* eslint-disable camelcase */
  /** iOS special param */
  o?: string,
  /** iOS special param */
  p?: string,
  /**  iOS transaction ID */
  txn?: string,
  /** Braintree payment nonce */
  payment_method_nonce?: string,
  /**  Redemption/book code */
  redemption_code?: string
}

const sliceName = 'registrationFlow'
const selectors = {}
const thunks = {
  register: createAsyncThunk(
    `${sliceName}/register`,
    async (extraParams: IExtraRegistrationParams = {}, { getState, dispatch }) => {
      const presenterLogin = selectActivePresenter(getState()).login
      await Registration.create(presenterLogin, {
        group_key: getGroupKey(presenterLogin),
        ...extraParams
      })
      await dispatch(profileSlice.fetch({ updateCurrent: true }))
    }
  )
}

export interface RegistrationFlow {
  [index: string]: string | boolean,
  requestedPage: 'intro' | 'purchase' | 'redeem',
  redemptionCode: string,
  inFlight: boolean,
  refreshing: boolean,
  error: string
}

const initialState: RegistrationFlow = {
  requestedPage: 'intro',
  redemptionCode: '',
  inFlight: false,
  refreshing: false,
  // Flag for complete/registered, as we dont refresh the activePresenter automatically
  complete: false,
  error: ''
}

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset () {
      return initialState
    },
    pageRequested (state, { payload }) {
      state.requestedPage = payload
    },
    redemptionCodeUpdated (state, { payload }) {
      state.redemptionCode = payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(thunks.register.pending, (state) => {
      state.inFlight = true
    })
    builder.addCase(thunks.register.fulfilled, (state) => {
      state.inFlight = false
      state.complete = true
    })
    builder.addCase(thunks.register.rejected, (state, action) => {
      state.inFlight = false
      state.error = action.error.message ?? 'Unknown error'
    })
    builder.addCase(activePresenterSlice.refreshRegistrationInfo.pending, (state) => {
      state.refreshing = true
    })
    builder.addCase(activePresenterSlice.refreshRegistrationInfo.fulfilled, (state) => {
      state.refreshing = false
    })
    builder.addCase(activePresenterSlice.refreshRegistrationInfo.rejected, (state, action) => {
      state.refreshing = false
      state.error = action.error.message ?? 'Unknown error'
    })
  }
})

const sliceExport = {
  name: sliceName,
  reducer: slice.reducer,
  ...slice.actions,
  ...selectors,
  ...thunks
}

export { sliceExport as slice }

export const selectRegisterFlow = (state: any): RegistrationFlow => state[sliceName]
